import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectCreditCard,
  selectCreditCardIdFromRoute,
} from 'src/app/AccountTransactions/redux/accountTransactions.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import ReportTransactionsBoxAndLinks from 'src/components/ReportTransactionsBoxAndLinks/ReportTransactionsBoxAndLinks'
import { TransactionReportType } from 'src/components/ReportTransactionsBoxAndLinks/reportTypes'
import { TransactionsTableLargeDesigns } from 'src/components/TransactionsDataGrid/TransactionsDataGrid'
import { useCurrentUser } from 'src/context/UserProvider/useUser'
import { TransactionFilterOptions } from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import { connectedReplace } from 'src/navigation/Lnk'
import { budgetTransactionsUrl } from 'src/navigation/urls'
import {
  selectOrganizationReceiptMgmtEnabledFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectActiveOrgIntegration } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { css } from 'styled-components'

const AccountTransactions: React.FC = () => {
  const dispatch = useBackgroundDispatch()

  const id = useSelector(selectCreditCardIdFromRoute)
  const creditCard = useSelector(selectCreditCard)
  const activeIntegration = useSelector(selectActiveOrgIntegration)
  const user = useCurrentUser()
  const receiptManagementEnabled = useSelector(
    selectOrganizationReceiptMgmtEnabledFromState,
  )
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const [initialLoad, setInitialLoad] = useState(true)

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 122)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 122)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  // redirect to budget transactions if budget
  useEffect(() => {
    if (creditCard?.isBudget) {
      dispatch(connectedReplace(budgetTransactionsUrl(creditCard.id)))
    }
  }, [creditCard])

  return (
    <div
      css={css`
        height: 100%;
      `}
      ref={ref}
      id={'ref'}
    >
      <BackgroundPageContent noStackingIndex>
        <ReportTransactionsBoxAndLinks
          context={TransactionReportType.Account}
          showIntegrationsLink
          design={TransactionsTableLargeDesigns.UserAndCard}
          initialLoad={initialLoad}
          setInitialLoad={setInitialLoad}
          creditCardId={id}
          visibleFilters={[
            TransactionFilterOptions.Date,
            TransactionFilterOptions.TransactionStatus,
            // using creditCard?.features.physicalTransactionsEnabled check until BE updates Regions cards to accurately set physicalClearingsEnabled
            ...(organization?.physicalClearingsEnabled ||
            creditCard?.features.physicalTransactionsEnabled
              ? [TransactionFilterOptions.TransactionOrigin]
              : []),
            TransactionFilterOptions.ExpenseCategoryStatus,
            TransactionFilterOptions.ExpenseCategoryLabels,
            ...(receiptManagementEnabled !== undefined
              ? [TransactionFilterOptions.ReceiptStatus]
              : []),
            ...(activeIntegration !== undefined &&
            user?.hasOrgBookkeeperPermissions
              ? [
                  TransactionFilterOptions.TransactionSyncStatus,
                  TransactionFilterOptions.ReceiptSyncStatus,
                ]
              : []),
            TransactionFilterOptions.CardUser,
            TransactionFilterOptions.Departments,
            TransactionFilterOptions.MerchantCategory,
          ]}
          clientHeight={clientHeight || 0}
        />
      </BackgroundPageContent>
    </div>
  )
}

export default AccountTransactions
